import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { TrendingSidebar } from "../components/TrendingSidebar";
import { CATEGORY_LABELS } from "../lib/constants";

// Section heads are set as an oversized index number plus a flush-left label,
// sitting under a full-bleed black rule.
function SectionHeader({ title, to, index }) {
    return (
        <div className="mb-8">
            <div className="rule" />
            <div className="flex items-end justify-between gap-4 mt-3">
                <div className="flex items-baseline gap-4">
                    {index && (
                        <span className="font-mono text-xs text-accent tabular-nums">
                            {String(index).padStart(2, "0")}
                        </span>
                    )}
                    <h2 className="font-display text-2xl sm:text-[2rem] font-black uppercase tracking-[-0.035em] leading-none">
                        {title}
                    </h2>
                </div>
                {to && (
                    <Link
                        to={to}
                        className="eyebrow text-dim hover:text-accent transition-colors duration-100 shrink-0 link-underline"
                    >
                        View all
                    </Link>
                )}
            </div>
        </div>
    );
}

function HomeSkeleton() {
    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <p className="eyebrow text-dim mb-6 animate-pulse-dot">Loading the newsroom…</p>
            <div className="h-1 bg-line w-full" />
            <div className="shimmer h-16 w-3/4 mt-5" />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-7">
                <div className="lg:col-span-8 shimmer aspect-[16/9]" />
                <div className="lg:col-span-4 space-y-3">
                    <div className="shimmer h-4 w-full" />
                    <div className="shimmer h-4 w-5/6" />
                    <div className="shimmer h-4 w-2/3" />
                </div>
            </div>
        </div>
    );
}

export default function Home() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/home").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <HomeSkeleton />;
    }

    if (!data || !data.featured) {
        return (
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="home-empty">
                <div className="rule" />
                <span className="eyebrow text-accent block mt-4">Standby</span>
                <h2 className="font-display text-4xl font-black uppercase tracking-[-0.035em] mt-3">
                    Fetching the latest stories
                </h2>
                <p className="mt-5 text-sm text-dim max-w-md leading-relaxed">
                    Articles are being pulled from The Guardian. Please check back in a moment.
                </p>
            </div>
        );
    }

    const { featured, latest, trending, sections } = data;

    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="home-page">
            {/* Lead story runs the full measure */}
            <ArticleCard article={featured} variant="hero" />

            {/* Latest — three columns of rule-separated items */}
            <div className="mt-20">
                <SectionHeader title="Latest News" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10">
                    {latest.slice(0, 6).map((a) => (
                        <ArticleCard key={a.id} article={a} variant="compact" />
                    ))}
                </div>
            </div>

            {/* Sections + trending */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mt-24">
                <div className="lg:col-span-8 space-y-20">
                    {Object.entries(sections).map(([cat, arts], i) => (
                        <section key={cat} data-testid={`home-section-${cat}`}>
                            <SectionHeader
                                title={CATEGORY_LABELS[cat] || cat}
                                to={`/category/${cat}`}
                                index={i + 1}
                            />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-12">
                                {arts.map((a) => (
                                    <ArticleCard key={a.id} article={a} />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
                <div className="lg:col-span-4">
                    <TrendingSidebar items={trending} />
                </div>
            </div>
        </div>
    );
}
